import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Facebook, Mail, MapPin, Sparkles, Send } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import { api } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — I'll reply as soon as I can. 🌸");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send, please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="section">
        <div className="mx-auto max-w-5xl text-center">
          <span className="chip">Contact</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">Let's chat treats</h1>
          <p className="mt-4 text-sakura-soft max-w-2xl mx-auto">Ready to make your event sweeter? Submit a custom order inquiry today — or drop a note below for anything else.</p>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="card-soft p-7">
              <h3 className="font-serif text-xl text-sakura-ink">Say hi</h3>
              <ul className="mt-4 space-y-3 text-sakura-ink">
                <li className="flex items-start gap-3">
                  <span className="h-10 w-10 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><Mail className="h-4 w-4" /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-sakura-500">Email</div>
                    <a href="mailto:sakuratreats.shop@gmail.com" className="text-sakura-ink hover:text-sakura-500" data-testid="contact-email">sakuratreats.shop@gmail.com</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-10 w-10 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><MapPin className="h-4 w-4" /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-sakura-500">Service Area</div>
                    <div>Tampa Bay / Spring Hill, Florida</div>
                  </div>
                </li>
              </ul>
              <div className="mt-5 flex items-center gap-2">
                <a href="https://www.instagram.com/sakuratreats_?igsh=MXFnNnM0NTl3YnBnMQ%3D%3D&utm_source=qr" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="contact-social-instagram" className="h-11 w-11 grid place-items-center rounded-full bg-sakura-400 text-white hover:bg-sakura-500 transition"><Instagram className="h-4 w-4" /></a>
                <a href="https://www.facebook.com/share/1J9pmaK1kw/?mibextid=wwXIfr" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="contact-social-facebook" className="h-11 w-11 grid place-items-center rounded-full bg-sakura-400 text-white hover:bg-sakura-500 transition"><Facebook className="h-4 w-4" /></a>
              </div>
            </div>

            <div className="card-soft p-7 bg-sakura-100/60 border-sakura-300/60">
              <h3 className="font-serif text-lg text-sakura-ink">Ready to order?</h3>
              <p className="mt-2 text-sm text-sakura-soft">Use the custom orders form for event details — it's the fastest way to get a quote.</p>
              <Link to="/custom-orders" className="btn-primary mt-4 w-full" data-testid="contact-order-cta"><Sparkles className="h-4 w-4" /> Start a Custom Order</Link>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-soft p-6 md:p-10" data-testid="contact-form">
            <h3 className="font-serif text-2xl text-sakura-ink">Send a quick note</h3>
            <p className="text-sakura-soft text-sm">For anything that's not a custom quote request.</p>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-semibold text-sakura-ink">Name</span>
                <input className="input-soft mt-1" value={form.name} onChange={set("name")} required data-testid="contact-name" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold text-sakura-ink">Email</span>
                <input type="email" className="input-soft mt-1" value={form.email} onChange={set("email")} required data-testid="contact-email-input" />
              </label>
            </div>
            <label className="block mt-4">
              <span className="text-sm font-semibold text-sakura-ink">Message</span>
              <textarea className="input-soft mt-1 min-h-[140px]" value={form.message} onChange={set("message")} required data-testid="contact-message" placeholder="Say hi…" />
            </label>
            <button type="submit" disabled={sending} className="btn-primary mt-6 disabled:opacity-60" data-testid="contact-submit">
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
